<!-- eslint-disable tailwindcss/no-custom-classname -->
<!-- eslint-disable vue/no-v-html -->
<template>
	<div class="relative m-0 size-full">
		<v-storyblok-component
			v-if="blok.video_blok?.length"
			:blok="blok.video_blok[0]"
		/>

		<!-- Wrapper div to allow pausing the video by clicking anywhere on it -->
		<div
			v-else-if="blok.video?.filename"
			:aria-label="isVideopaused ? t('play') : t('pause')"
			:aria-pressed="isVideopaused"
			class="relative size-full cursor-default"
			tabindex="0"
			role="button"
			@click.stop.prevent="updatePausedState(!isVideopaused)"
			@keydown.enter.space.stop.prevent="updatePausedState(!isVideopaused)"
		>
			<!-- eslint-disable-next-line vuejs-accessibility/media-has-caption -->
			<video
				ref="nativeVideo"
				:autoplay="preferredMotion !== 'reduce'"
				loop
				muted
				playsinline
				:alt="videoAlt"
				:controls="blok.player_show_controls ?? false"
				class="size-full object-cover"
				@pause.prevent="updatePausedState(true)"
				@play.prevent="updatePausedState(false)"
			>
				<source
					v-if="blok.video.filename.endsWith('.webm')"
					type="video/webm"
					:src="blok.video.filename"
				/>

				<source v-else type="video/mp4" :src="blok.video.filename" />
			</video>
		</div>

		<nuxt-link
			v-if="url && blok.cta_title"
			:to="url"
			class="z-20 flex h-8 w-full flex-col items-center justify-center bg-(--link-background-color) text-center font-bold text-(--link-color) uppercase hover:underline md:h-14"
			:style="{
				'--link-color': linkColor,
				'--link-background-color': linkBackgroundColor,
			}"
		>
			{{ blok.cta_title }}
		</nuxt-link>
	</div>
</template>

<script setup lang="ts">
import {usePreferredReducedMotion} from "@vueuse/core";
import type {BlokComponent} from "@pkg/shared/types";
import formatSbUrl from "@pkg/shared/utils/format-sb-url";

const properties = defineProps<{
	blok: Extract<BlokComponent, {component: "sb-cta-video"}>;
}>();

// Same script logic as in sb-hero-video.vue

const nativeVideo = ref<HTMLVideoElement | null>(null);
const isVideopaused = ref(false);
const preferredMotion = usePreferredReducedMotion();

const {t} = useI18n();

const url = computed(() => formatSbUrl(properties.blok.cta_link));

const linkColor = computed<string | undefined>(
	() => properties.blok.link_color?.color ?? "#FFF",
);

const linkBackgroundColor = computed<string | undefined>(
	() => properties.blok.link_background_color?.color ?? "#000",
);

const videoAlt = computed(() => properties.blok.player_alt || t("video-alt"));

// Function to update the paused state of the video player.
const updatePausedState = (isPaused: boolean) => {
	isVideopaused.value = isPaused;
	if (nativeVideo.value) {
		if (isPaused) {
			nativeVideo.value.pause();
		} else {
			void nativeVideo.value.play();
		}
	}
};

onMounted(() => {
	if (preferredMotion.value === "reduce") {
		updatePausedState(true);
	}
});

watch(preferredMotion, (preference) => {
	if (preference === "reduce") {
		updatePausedState(true);
	}
});
</script>

<i18n lang="json">
{
	"et": {
		"play": "Mängi video",
		"pause": "Paus video"
	},
	"fi": {
		"play": "Toista video",
		"pause": "Pysäytä video"
	}
}
</i18n>
